import { z } from 'zod';

/**
 * 동의 항목 — ERD `consents.type` 과 1:1 로 대응한다.
 *
 * 타입명에 매체가 박혀 있는 것(`MARKETING_EMAIL`)은 의도된 설계다.
 * 이메일 동의로 다른 매체를 보내면 정보통신망법 위반이므로, **화면 문구도 매체를 특정해야 한다**
 * → 14. 1차 오픈 범위 §5.2
 */
export const consentTypeSchema = z.enum([
  'AGE_OVER_14',
  'TERMS_OF_SERVICE',
  'PRIVACY_POLICY',
  'MARKETING_EMAIL',
]);

export type ConsentType = z.infer<typeof consentTypeSchema>;

/** 선택 동의에 펼쳐 보일 고지 한 줄. 접어 두면 아무도 안 편다. */
export type ConsentNotice = { readonly term: string; readonly value: string };

export type ConsentTerm = {
  readonly type: ConsentType;
  readonly label: string;
  readonly required: boolean;
  /** 상세 고지 경로. 라우터에 실재하는 경로만 적는다 — 죽은 링크는 고지가 아니다 */
  readonly href?: string;
  /** 선택 항목은 접지 않고 화면에 펼친다 */
  readonly notices?: readonly ConsentNotice[];
  /** 거부해도 불이익이 없음을 화면에 명시한다 — 개보법 요건이자 동의율 장치다 */
  readonly noPenaltyNote?: string;
};

/**
 * 화면에 그대로 나가는 동의 문구.
 *
 * 문구가 바뀌면 `consents.version` 이 올라가야 한다 — 어떤 문구에 동의했는지가 증빙의 전부다.
 */
export const CONSENT_VERSION = '2026-08-27';

export const CONSENT_TERMS: readonly ConsentTerm[] = [
  {
    type: 'AGE_OVER_14',
    label: '만 14세 이상입니다',
    required: true,
  },
  {
    type: 'TERMS_OF_SERVICE',
    label: '이용약관에 동의합니다',
    required: true,
    href: '/terms',
  },
  {
    type: 'PRIVACY_POLICY',
    label: '개인정보 수집·이용에 동의합니다',
    required: true,
    href: '/privacy',
  },
  {
    type: 'MARKETING_EMAIL',
    label: '새 자료·클래스 소식을 이메일로 받겠습니다',
    required: false,
    href: '/marketing-consent',
    notices: [
      { term: '전송 매체', value: '이메일' },
      { term: '보내는 내용', value: '새 자료·클래스 출시, 할인·이벤트 안내' },
      { term: '수집 항목', value: '이메일 주소' },
      { term: '보유 기간', value: '수신 동의를 철회하실 때까지' },
    ],
    noPenaltyNote: '동의하지 않으셔도 자료를 받는 데 아무 제한이 없습니다.',
  },
];

export const REQUIRED_CONSENT_TERMS = CONSENT_TERMS.filter((term) => term.required);
export const OPTIONAL_CONSENT_TERMS = CONSENT_TERMS.filter((term) => !term.required);

/** 필수 동의가 모두 채워졌는지 — 제출 버튼 활성의 유일한 기준이다. */
export function hasAllRequiredConsents(granted: ReadonlySet<ConsentType>): boolean {
  return REQUIRED_CONSENT_TERMS.every((term) => granted.has(term.type));
}
